/* ═══════════════════════════════════════════════════════════════
   taskFilterFields — the ONE declaration of Tasks' standard-Filter-menu
   fields (Status / Priority / Project / Assignee / Due — Decision 3) and
   the matching per-task value resolver. TasksTable's DataTable columns
   declare the identical filterType/filterOptions/filterValue so the
   List's own Filter button and this module produce the SAME field list;
   TasksBoard has no DataTable of its own, so it evaluates the SAME lifted
   `TableFilterState` against this resolver directly (matchesTaskFilters) —
   one predicate, both views (Decision 2).
   ═══════════════════════════════════════════════════════════════ */
import type { Project, Task, TeamMember } from '../../../shared/types';
import type { FilterField } from '../../components/FilterBuilder';
import { matchesFilterState, type TableFilterState } from '../../components/filterModel';
import { isArchived } from '../../lib/archive';
import { sanitizeProjectName } from '../projects/helpers';
import { STATUS_COLS, PRIORITY_LABELS } from './types';

export interface TaskFilterCtx {
  getProjectIdsForTask: (taskId: string) => string[];
  getAssigneeIdsForTask: (taskId: string) => string[];
}

/** Field keys match the TasksTable column keys 1:1 so a saved filter means
 *  the same thing whichever view applied it. */
export function buildTaskFilterFields(projects: Project[], members: TeamMember[]): FilterField[] {
  return [
    {
      key: 'status',
      label: 'Status',
      kind: 'multiselect',
      options: STATUS_COLS.map((s) => ({ value: s.id, label: s.label })),
    },
    {
      key: 'priority',
      label: 'Priority',
      kind: 'multiselect',
      options: Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'project',
      label: 'Project',
      kind: 'multiselect',
      options: projects
        .filter((p) => p.status === 'active' && !isArchived(p))
        .map((p) => ({
          value: p.id,
          label: p.projectNumber || sanitizeProjectName(p.name) || 'Untitled',
        })),
    },
    {
      key: 'assignees',
      label: 'Assignee',
      kind: 'multiselect',
      options: members.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name })),
    },
    { key: 'dueDate', label: 'Due', kind: 'date' },
  ];
}

export function resolveTaskFilterField(
  task: Task,
  field: string,
  ctx: TaskFilterCtx
): string | number | string[] | undefined {
  switch (field) {
    case 'status':
      return task.status;
    case 'priority':
      return task.priority;
    case 'dueDate':
      return task.dueDate || '';
    case 'project':
      return ctx.getProjectIdsForTask(task.id);
    case 'assignees':
      return ctx.getAssigneeIdsForTask(task.id);
    default:
      return undefined;
  }
}

export function matchesTaskFilters(
  task: Task,
  state: TableFilterState,
  ctx: TaskFilterCtx
): boolean {
  return matchesFilterState(state, (field) => resolveTaskFilterField(task, field, ctx));
}
